import { BootstrapBreakpoint } from '@/constants/BootstrapBreakpoint';
import { createGlobalState } from '@vueuse/shared';
import type { ComputedRef, Ref } from 'vue';
import { computed, ref } from 'vue';

export const useScreenSizes = createGlobalState(
  (): {
    scrollbarWidth: Ref<number>;
    doesScrollbarTakeUpSpace: ComputedRef<boolean>;
    screenSize: Ref<BootstrapBreakpoint>;
    millimeterRulerHeight: Ref<number>;
    convertPxToMm: (px: number) => number;
    convertMmToPx: (mm: number) => number;
    isSmallScreen: ComputedRef<boolean>;
    isLargeScreen: ComputedRef<boolean>;
  } => {
    const scrollbarWidth = ref(0);

    const doesScrollbarTakeUpSpace = computed(() => scrollbarWidth.value > 0);

    const screenSize = ref(BootstrapBreakpoint.XS);

    const isSmallScreen = computed(() => screenSize.value < BootstrapBreakpoint.LG);
    const isLargeScreen = computed(() => screenSize.value >= BootstrapBreakpoint.LG);

    /**
     * Sane first value taken from https://www.unitconverters.net/typography/pixel-x-to-millimeter.htm
     *
     * Adjusted automatically on platform page load to be completely accurate.
     */
    const millimeterRulerHeight = ref(26.4583);

    const convertPxToMm = (px: number): number => Math.floor(px / (millimeterRulerHeight.value / 100));
    const convertMmToPx = (mm: number): number => Math.floor(mm * (millimeterRulerHeight.value / 100));

    return {
      scrollbarWidth,
      doesScrollbarTakeUpSpace,
      screenSize,
      millimeterRulerHeight,
      convertPxToMm,
      convertMmToPx,
      isSmallScreen,
      isLargeScreen,
    };
  },
);

/**
 * Assumes a base font size of 16px
 */
export const convertRemToPx = (rawRem: string): number => {
  // strip the 'rem' from the string
  return parseFloat(rawRem) * 16;
};
